import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { IoArrowForward, IoBook, IoDocumentText, IoOpenOutline } from "react-icons/io5";
import Main from "../../components/layout/Main";

// The app's Help Center and User Guide (app/screens/HelpCenterScreen.js,
// UserGuide.js): the guides list from manuals.hashh.io, one level per page
// (guide › device category › device), and a device's PDF opens in a new tab.
// Each level is a step in the browser's history, so Back goes up a level.
const GUIDES_URL = "https://manuals.hashh.io/help-center/";
const PDF_URL = "https://manuals.hashh.io/pdf/";

// Every level keeps its items under its own key (guide, category or devices);
// a device has the PDF's file name instead.
const childrenOf = (node) => node?.guide ?? node?.category ?? node?.devices ?? [];
const TITLES = ["Help Center", "Select a Guide", "Select a Device Category", "Select a Device"];

function HelpCenter() {
  const history = useHistory();
  const { pathname, state } = useLocation();
  const trail = Array.isArray(state?.trail) ? state.trail : [];
  const [guides, setGuides] = useState(null);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let current = true;
    setError(null);
    fetch(GUIDES_URL)
      .then((response) => (response.ok ? response.json() : Promise.reject(response)))
      .then((data) => current && setGuides(Array.isArray(data) ? data : []))
      .catch(() => current && setError("Couldn't load the guides. Check the internet connection."));
    return () => {
      current = false;
    };
  }, [attempt]);

  // The level the person has picked their way down to.
  const node = trail.reduce((parent, index) => childrenOf(parent)[index], { guide: guides ?? [] });
  const items = childrenOf(node);
  const title = TITLES[trail.length] ?? node?.name ?? TITLES[0];
  const open = (index) => history.push(pathname, { trail: [...trail, index] });

  return (
    <Main header="stack" title={title}>
      {error ? (
        <div className="hx-empty" role="alert">
          {error}
          <button type="button" className="hx-btn" onClick={() => setAttempt(attempt + 1)}>
            Try again
          </button>
        </div>
      ) : guides === null ? (
        <div className="hx-skel" style={{ height: 240, marginTop: 16 }} aria-label="Loading" />
      ) : items.length ? (
        <ul className="hx-list">
          {items.map((item, index) => {
            const name = item?.name || "Guide";
            if (item?.file) {
              return (
                <li key={`${name}-${index}`}>
                  <a
                    className="hx-list-item"
                    href={`${PDF_URL}${encodeURIComponent(item.file)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <IoDocumentText aria-hidden="true" />
                    <span className="hx-list-text">
                      <span className="hx-list-title">{name}</span>
                      <span className="hx-list-sub">PDF, opens in a new tab</span>
                    </span>
                    <IoOpenOutline className="hx-list-arrow" aria-hidden="true" />
                  </a>
                </li>
              );
            }
            const ready = childrenOf(item).length > 0;
            return (
              <li key={`${name}-${index}`}>
                <button type="button" className="hx-list-item" disabled={!ready} onClick={() => open(index)}>
                  <IoBook aria-hidden="true" />
                  <span className="hx-list-text">
                    <span className="hx-list-title">{name}</span>
                    {ready ? null : <span className="hx-list-sub">Coming soon</span>}
                  </span>
                  {ready ? <IoArrowForward className="hx-list-arrow" aria-hidden="true" /> : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="hx-empty">Nothing here yet.</div>
      )}
    </Main>
  );
}

export default HelpCenter;
